import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Facebook, Instagram, Linkedin, Twitter, MessageCircle, Mail, MessageSquare, Copy, Check, QrCode, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CANONICAL_ORIGIN = process.env.REACT_APP_CANONICAL_ORIGIN || "https://websitetech.pro";

// Share links must point to the public production site, not the preview/localhost origin.
function canonicalize(u) {
  try {
    const x = new URL(u, CANONICAL_ORIGIN);
    return CANONICAL_ORIGIN.replace(/\/$/, "") + x.pathname + x.search;
  } catch {
    return u;
  }
}

export default function ShareBar({ url, title = "", shortUrl, onResolved }) {
  const [copied, setCopied] = useState(false);
  const [generated, setGenerated] = useState(shortUrl || "");
  const [loading, setLoading] = useState(false);
  const shareUrl = canonicalize(url);
  const link = shortUrl || generated || shareUrl;
  const enc = encodeURIComponent(link);
  const encT = encodeURIComponent(title);

  // Derive the server-side redirect URL (go_url) from a splink short URL.
  const deriveGo = (su) => {
    try { const u = new URL(su); return `${u.origin}/api/go${u.pathname}`; } catch { return null; }
  };

  // Auto-generate a SpLink.pro short URL when one wasn't supplied.
  useEffect(() => {
    if (shortUrl) { onResolved?.({ short_url: shortUrl, go_url: deriveGo(shortUrl) }); return; }
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${BACKEND}/api/shorten`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ long_url: canonicalize(url) }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.short_url) { setGenerated(d.short_url); onResolved?.(d); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [url, shortUrl]);

  const targets = [
    { name: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${encT}%20${enc}`, color: "#25D366" },
    { name: "Email", icon: Mail, href: `mailto:?subject=${encT}&body=${enc}`, color: "#64748B" },
    { name: "Text", icon: MessageSquare, href: `sms:?&body=${encT}%20${enc}`, color: "#0EA5E9" },
    { name: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc}`, color: "#1877F2" },
    { name: "X", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${enc}&text=${encT}`, color: "#0F172A" },
    { name: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc}`, color: "#0A66C2" },
    { name: "Instagram", icon: Instagram, href: `https://www.instagram.com/`, color: "#E4405F" },
  ];

  const copy = () => {
    navigator.clipboard?.writeText(link);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="border border-slate-200 rounded-2xl p-6 bg-slate-50" data-testid="share-bar">
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="flex-1">
          <p className="overline mb-3">Share this page</p>
          <div className="flex flex-wrap gap-2">
            {targets.map((t) => (
              <a
                key={t.name}
                href={t.href}
                target="_blank"
                rel="noreferrer"
                title={`Share on ${t.name}`}
                data-testid={`share-${t.name.toLowerCase()}`}
                className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center hover:-translate-y-0.5 transition-transform"
                style={{ color: t.color }}
              >
                <t.icon size={18} />
              </a>
            ))}
          </div>
          <div className="mt-5">
            <p className="overline mb-2">Short link</p>
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 max-w-md">
              {loading ? (
                <span className="text-sm text-slate-400 flex items-center gap-2 flex-1"><Loader2 size={14} className="animate-spin" /> Generating short link…</span>
              ) : (
                <span className="text-sm text-slate-600 truncate flex-1 font-mono" data-testid="short-link-value">{link}</span>
              )}
              <button onClick={copy} className="text-slate-400 hover:text-brand" data-testid="copy-link" disabled={loading}>
                {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">Powered by SpLink.pro · branded short links with click analytics</p>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-white border border-slate-200 rounded-xl p-3 inline-block">
            <QRCodeSVG value={link} size={112} fgColor="#0F172A" />
          </div>
          <p className="text-[11px] text-slate-500 mt-2 flex items-center justify-center gap-1"><QrCode size={12} /> Scan to open</p>
        </div>
      </div>
    </div>
  );
}
